import { useState, useEffect, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  useForm,
  FormProvider,
  useFieldArray,
  useWatch,
  Controller,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Typography,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Snackbar,
  Alert,
  Tooltip,
  Checkbox,
  FormControlLabel,
  ListItemText,
  Autocomplete,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PersonAddAlt as PersonAddIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { useAuth } from "../../../auth";
import { customInstanceDocumentos } from "../../../api/axios-client";
import ListLayout from "../../../components/Layout/ListLayout";
import { FormTextField } from "../../../forms/FormTextField";
import { useCreateSignatureBatch } from "../../../api/documentos/endpoints";
import { BatchSignatureInput } from "../../../api/documentos/model/batchSignatureInput";
import { DocumentSelectionDialog } from "./components/DocumentSelectionDialog";
import type { DocumentDto } from "../../../api/documentos/model";
import { VisualizeAction } from "../documents/components/VisualizeAction";
import { isParaguayDomain } from "../../../utils/envUtils";

const createSignerSchema = (t: TFunction) =>
  z
    .object({
      name: z.string().min(1, t("validation.nameRequired")),
      email: z
        .string()
        .email(t("validation.invalidEmail"))
        .min(1, t("validation.emailRequired")),
      document: z.string().min(1, t("validation.documentRequired")),
      signatureType: z.string().min(1, t("validation.signatureTypeRequired")),
      phoneNumber: z.string().optional(),
      signatureOrder: z
        .preprocess(
          (val) =>
            val === "" || val === null || val === undefined
              ? undefined
              : Number(val),
          z
            .number()
            .int()
            .positive(
              t("signatures.validation.invalidOrder", "A ordem deve ser maior que zero"),
            )
            .optional(),
        ),
    })
    .refine(
      (data) => {
        if (data.signatureType === "3") {
          return !!data.phoneNumber && isValidE164Phone(data.phoneNumber);
        }
        return true;
      },
      {
        message: t("signatures.validation.invalidPhone"),
        path: ["phoneNumber"],
      },
    );

const isValidE164Phone = (value?: string) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 15;
};

const e164PhoneMask = (value?: string) => {
  if (!value) return "";
  const hasPlus = value.startsWith("+");
  const digits = String(value).replace(/\D/g, "");
  if (!digits) return hasPlus ? "+" : "";
  return `+${digits.substring(0, 15)}`;
};

const documentSchema = z.object({
  id: z.number({ message: "ID required" }),
  name: z.string().optional(),
  fileName: z.string().optional(),
  temporaryUrl: z.string().optional(),
  extensionName: z.string().optional(),
});

const createObserverSchema = (t: TFunction) =>
  z.object({
    name: z.string().min(1, t("validation.nameRequired")),
    email: z
      .string()
      .email(t("validation.invalidEmail"))
      .min(1, t("validation.emailRequired")),
    documentos: z.array(z.string()).optional(),
  });

const createEnvelopeSchemaFn = (t: TFunction) =>
  z.object({
    title: z.string().min(1, t("validation.envelopeTitleRequired")),
    description: z.string().optional(),
    signatureDueDate: z.string().optional().nullable(),
    signers: z
      .array(createSignerSchema(t))
      .min(1, t("validation.addAtLeastOneSigner")),
    observers: z.array(createObserverSchema(t)).optional(),
    documents: z
      .array(documentSchema)
      .min(1, t("validation.addAtLeastOneDocument")),
    disableEmailNotification: z.boolean().optional(),
  });

type CreateEnvelopeFormData = z.infer<
  ReturnType<typeof createEnvelopeSchemaFn>
>;

const createSearchSchema = z.object({
  documentId: z.coerce.string().optional(),
});

export const Route = createFileRoute("/__auth/signatures/create")({
  component: CreateEnvelopeComponent,
  validateSearch: (search) => createSearchSchema.parse(search),
  staticData: {
    title: "routes.createEnvelope",
  },
});

const getDocTitleWithExtension = (doc: any) => {
  let title =
    doc.file?.title || doc.name || `Documento ${doc.id || doc.code || ""}`;
  const separatorIndex = title.indexOf(" - ");
  if (separatorIndex !== -1 && !doc.file?.title) {
    title = title.substring(separatorIndex + 3);
  }
  const ext =
    doc.extension?.name ||
    doc.file?.extension?.name ||
    doc.extensionName ||
    "";
  if (!ext) return title;
  if (title.toLowerCase().endsWith(ext.toLowerCase())) {
    return title;
  }
  const formattedExt = ext.startsWith(".") ? ext : `.${ext}`;
  return `${title}${formattedExt}`;
};

function CreateEnvelopeComponent() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const tenant = user?.tenant || "default-domain";
  const queryClient = useQueryClient();
  const createSignatureBatchMutation = useCreateSignatureBatch();

  const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (
    message: string,
    severity: "success" | "error" = "success",
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const methods = useForm<CreateEnvelopeFormData>({
    resolver: zodResolver(createEnvelopeSchemaFn(t)) as any,
    defaultValues: {
      title: "",
      description: "",
      signatureDueDate: "",
      signers: [],
      observers: [],
      documents: [],
      disableEmailNotification: false,
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    getValues,
  } = methods;

  const {
    fields: signerFields,
    append: appendSigner,
    remove: removeSigner,
  } = useFieldArray({
    control,
    name: "signers",
  });

  const {
    fields: documentFields,
    remove: removeDocument,
    replace: replaceDocuments,
  } = useFieldArray({
    control,
    name: "documents",
  });

  const {
    fields: observerFields,
    append: appendObserver,
    remove: removeObserver,
  } = useFieldArray({
    control,
    name: "observers",
  });

  const watchedSigners = useWatch({
    control,
    name: "signers",
  });

  const watchedDocuments = useWatch({
    control,
    name: "documents",
  });

  const watchedObservers = useWatch({
    control,
    name: "observers",
  });

  const documentOptions = useMemo(() => {
    return (watchedDocuments || []).map((doc) => doc.name || String(doc.id));
  }, [
    JSON.stringify(
      watchedDocuments?.map((d) => ({ id: d.id, name: d.name })) || [],
    ),
  ]);

  useEffect(() => {
    if (!search.documentId || !tenant) return;
    const docId = parseInt(search.documentId);
    if (isNaN(docId)) return;
    if (getValues("documents").length > 0) return;

    const fetchDoc = async () => {
      try {
        const doc = await customInstanceDocumentos<any>(
          `/${tenant}/documents/${docId}`,
          { method: "GET" },
        );
        if (doc) {
          const docFileName = getDocTitleWithExtension(doc);
          replaceDocuments([
            {
              id: doc.id,
              name: doc.name
                ? `${doc.code} - ${doc.name}`
                : `Documento ${doc.code || doc.id}`,
              fileName: docFileName,
              temporaryUrl: "",
              extensionName: doc.extension?.name,
            },
          ]);
        }
      } catch (err) {
        console.error("Failed to fetch document for pre-fill:", err);
      }
    };
    fetchDoc();
  }, [search.documentId, tenant]);

  const isUserAlreadySigner = watchedSigners?.some(
    (s) =>
      (user?.email && s.email === user.email) ||
      (user?.cpf && s.document === user.cpf),
  );

  const handleAddMeAsSigner = () => {
    if (!user) return;
    const currentSigners = getValues("signers");
    const hasType3 = currentSigners.some((s) => s.signatureType === "3");
    appendSigner({
      name: user.name || "",
      email: user.email || "",
      document: user.cpf || "",
      signatureType: hasType3 ? "3" : "0",
      phoneNumber: "",
    });
  };

  const handleAddNewSigner = () => {
    const currentSigners = getValues("signers");
    const hasType3 = currentSigners.some((s) => s.signatureType === "3");
    appendSigner({
      name: "",
      email: "",
      document: "",
      signatureType: hasType3 ? "3" : "0",
      phoneNumber: "",
    });
  };

  const handleAddNewObserver = () => {
    appendObserver({
      name: "",
      email: "",
      documentos: [],
    });
  };

  const handleSignatureTypeChange = (index: number, value: string) => {
    const currentSigners = getValues("signers");
    if (value === "3") {
      currentSigners.forEach((_, i) => {
        methods.setValue(`signers.${i}.signatureType`, "3", {
          shouldValidate: true,
        });
      });
    } else {
      const hasType3 = currentSigners.some((s) => s.signatureType === "3");
      if (hasType3) {
        currentSigners.forEach((_, i) => {
          methods.setValue(`signers.${i}.signatureType`, value, {
            shouldValidate: true,
          });
        });
      } else {
        methods.setValue(`signers.${index}.signatureType`, value, {
          shouldValidate: true,
        });
      }
    }
  };

  const handleDocumentsSelected = (selectedDocs: DocumentDto[]) => {
    replaceDocuments(
      selectedDocs.map((doc: any) => {
        const docFileName = getDocTitleWithExtension(doc);
        const isFreshApiDoc =
          !!doc.extension || (!!doc.code && doc.extensionName === undefined);

        if (isFreshApiDoc) {
          return {
            id: doc.id!,
            name: doc.name
              ? `${doc.code} - ${doc.name}`
              : `Documento ${doc.code || doc.id}`,
            fileName: docFileName,
            temporaryUrl: "",
            extensionName: doc.extension?.name,
          };
        }

        return {
          id: doc.id!,
          name: doc.name || `Documento ${doc.id}`,
          fileName: doc.fileName || docFileName,
          temporaryUrl: doc.temporaryUrl || "",
          extensionName: doc.extensionName,
        };
      }),
    );
  };

  const onSubmit = async (data: CreateEnvelopeFormData) => {
    if (!user) return;

    try {
      const payload: BatchSignatureInput = {
        title: data.title,
        description: data.description,
        signatureDueDate: data.signatureDueDate
          ? `${data.signatureDueDate}T23:59:59Z`
          : (() => {
              const date = new Date();
              date.setDate(date.getDate() + 7);
              const yyyy = date.getFullYear();
              const mm = String(date.getMonth() + 1).padStart(2, "0");
              const dd = String(date.getDate()).padStart(2, "0");
              return `${yyyy}-${mm}-${dd}T23:59:59Z`;
            })(),
        authorName: user.name || "",
        authorEmail: user.email || "",
        authorDocument: user.cpf || "00000000000",
        signers: data.signers.map((s) => ({
          ...s,
          signatureOrder:
            s.signatureOrder !== undefined &&
            s.signatureOrder !== null &&
            String(s.signatureOrder).trim() !== ""
              ? Number(s.signatureOrder)
              : undefined,
          phoneNumber:
            s.signatureType === "3" ? s.phoneNumber?.trim() : undefined,
        })) as any,
        observers: data.observers?.map((obs) => ({
          name: obs.name,
          email: obs.email,
          documentos: (obs.documentos || []).map((docStr) => {
            const matchedDoc = (watchedDocuments as any[])?.find(
              (d) => d.name === docStr || d.fileName === docStr,
            );
            if (matchedDoc?.fileName) {
              return matchedDoc.fileName;
            }
            return getDocTitleWithExtension({ name: docStr });
          }),
        })),
        documents: data.documents.map((d) => ({
          id: d.id,
          name: d.name,
          temporaryUrl: d.temporaryUrl,
        })),
        signatureType: "0",
        disableEmailNotification: !!data.disableEmailNotification,
      };

      await createSignatureBatchMutation.mutateAsync({
        tenantId: tenant,
        data: payload,
      });

      await queryClient.invalidateQueries({
        queryKey: [`/${tenant}/documents/covers`],
      });

      navigate({
        to: "/signatures/envelopes",
        search: { page: 1, size: 10 },
      });
    } catch (error: any) {
      console.error("Failed to create envelope:", error);
      const errMsg =
        error?.response?.data?.message ||
        error?.message ||
        t("signatures.createError") ||
        "Erro ao criar o envelope";
      showSnackbar(errMsg, "error");
    }
  };

  return (
    <ListLayout
      title={t("signatures.createEnvelope")}
      subtitle={t("signatures.createSubtitle")}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 4 },
          mt: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t("signatures.envelopeData")}
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <FormTextField
                      name="title"
                      label={t("signatures.envelopeName")}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormTextField
                      name="description"
                      label={t("signatures.descriptionOptional")}
                      fullWidth
                      multiline
                      rows={3}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormTextField
                      name="signatureDueDate"
                      label={t("signatures.expirationDate")}
                      type="date"
                      fullWidth
                      slotProps={{ inputLabel: { shrink: true } }}
                      helperText={t("signatures.expirationDateObservation")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="disableEmailNotification"
                      control={control}
                      render={({ field }) => (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                              />
                            }
                            label={t("signatures.disableEmailNotification") || "Desativar notificações de e-mail"}
                          />
                          <Tooltip
                            title={t("signatures.disableEmailNotificationTooltip") || "As notificações de e-mail serão desativadas"}
                            arrow
                          >
                            <InfoIcon
                              color="action"
                              sx={{ fontSize: 20, cursor: "pointer", ml: -0.5 }}
                            />
                          </Tooltip>
                        </Box>
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">{t("documents.title")}</Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={() => setIsDocumentDialogOpen(true)}
                  >
                    {t("signatures.addDocument")}
                  </Button>
                </Box>

                {documentFields.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic", mb: 2 }}
                  >
                    {t("signatures.noDocumentsAdded")}
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {documentFields.map((doc, index) => (
                      <Grid size={{ xs: 12 }} key={doc.id}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            p: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <Box sx={{ flexGrow: 1, mr: 2 }}>
                            <Typography variant="subtitle1" fontWeight={500}>
                              {getValues(`documents.${index}.name`)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <VisualizeAction
                              documentId={getValues(`documents.${index}.id`)}
                              documentName={getValues(
                                `documents.${index}.name`,
                              )}
                              tenantId={tenant}
                              extension={getValues(
                                `documents.${index}.extensionName`,
                              )}
                            />
                            <IconButton
                              color="error"
                              onClick={() => removeDocument(index)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
                {methods.formState.errors.documents && (
                  <Typography variant="caption" color="error">
                    {methods.formState.errors.documents.root?.message ||
                      methods.formState.errors.documents.message}
                  </Typography>
                )}
              </Box>

              <Divider />

              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">
                    {t("signatures.signers")}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<PersonAddIcon />}
                      size="small"
                      onClick={handleAddMeAsSigner}
                      disabled={isUserAlreadySigner}
                    >
                      {t("signatures.addMe")}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      size="small"
                      onClick={handleAddNewSigner}
                    >
                      {t("signatures.newSigner")}
                    </Button>
                  </Box>
                </Box>

                {signerFields.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic", mb: 2 }}
                  >
                    {t("signatures.noSignersAdded")}
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {signerFields.map((signer, index) => (
                      <Grid size={{ xs: 12 }} key={signer.id}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 2,
                            p: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                            <Grid size={{ xs: 12, md: 1.5 }}>
                              <Tooltip
                                title={t(
                                  "signatures.signatureOrderTooltip",
                                  "Define a ordem em que os assinantes assinarão o documento. O preenchimento não é obrigatório.",
                                )}
                                arrow
                              >
                                <Box sx={{ width: "100%" }}>
                                  <FormTextField
                                    name={`signers.${index}.signatureOrder`}
                                    label={t("signatures.signatureOrder", "Ordem")}
                                    type="number"
                                    fullWidth
                                    size="small"
                                    inputProps={{ min: 1, step: 1 }}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <InfoIcon
                                            color="action"
                                            sx={{ fontSize: 16, cursor: "pointer" }}
                                          />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Box>
                              </Tooltip>
                            </Grid>
                            <Grid size={{ xs: 12, md: 2.6 }}>
                              <FormTextField
                                name={`signers.${index}.name`}
                                label={t("signatures.fullName")}
                                fullWidth
                                size="small"
                                required
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 2.6 }}>
                              <FormTextField
                                name={`signers.${index}.document`}
                                label={
                                  isParaguayDomain()
                                    ? t("signatures.documentParaguay")
                                    : t("signatures.document")
                                }
                                fullWidth
                                size="small"
                                required
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 2.6 }}>
                              <FormTextField
                                name={`signers.${index}.email`}
                                label="E-mail"
                                type="email"
                                fullWidth
                                size="small"
                                required
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 2.7 }}>
                              <FormControl
                                fullWidth
                                size="small"
                                error={
                                  !!methods.formState.errors.signers?.[index]
                                    ?.signatureType
                                }
                              >
                                <InputLabel>
                                  {t("signatures.signatureType")}
                                </InputLabel>
                                <Select
                                  label={t("signatures.signatureType")}
                                  {...methods.register(
                                    `signers.${index}.signatureType`,
                                  )}
                                  value={
                                    watchedSigners?.[index]?.signatureType ||
                                    "0"
                                  }
                                  onChange={(e) =>
                                    handleSignatureTypeChange(
                                      index,
                                      e.target.value as string,
                                    )
                                  }
                                >
                                  <MenuItem value="0">
                                    {t("signatures.electronic")}
                                  </MenuItem>
                                  <MenuItem value="1">
                                    {t("signatures.digitalToken")}
                                  </MenuItem>
                                  <MenuItem value="2">
                                    {t("signatures.electronicOrDigital")}
                                  </MenuItem>
                                  <MenuItem value="3">
                                    {t("signatures.digitalParaguay")}
                                  </MenuItem>
                                </Select>
                                {methods.formState.errors.signers?.[index]
                                  ?.signatureType && (
                                  <FormHelperText>
                                    {
                                      methods.formState.errors.signers?.[index]
                                        ?.signatureType?.message
                                    }
                                  </FormHelperText>
                                )}
                              </FormControl>
                            </Grid>
                            {watchedSigners?.[index]?.signatureType === "3" && (
                              <Grid size={{ xs: 12, md: 3 }}>
                                <FormTextField
                                  name={`signers.${index}.phoneNumber`}
                                  label={t("signatures.phone")}
                                  placeholder="+5511999999999"
                                  fullWidth
                                  size="small"
                                  required
                                  transform={e164PhoneMask}
                                />
                              </Grid>
                            )}
                          </Grid>
                          <IconButton
                            color="error"
                            onClick={() => removeSigner(index)}
                            sx={{ mt: 0.5 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
                {methods.formState.errors.signers && (
                  <Typography variant="caption" color="error">
                    {methods.formState.errors.signers.root?.message ||
                      methods.formState.errors.signers.message}
                  </Typography>
                )}
              </Box>

              <Divider />

              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">
                    {t("signatures.observers", "Observadores")}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={handleAddNewObserver}
                  >
                    {t("signatures.newObserver", "Novo observador")}
                  </Button>
                </Box>

                {observerFields.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic", mb: 2 }}
                  >
                    {t("signatures.noObserversAdded", "Nenhum observador adicionado.")}
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {observerFields.map((observer, index) => (
                      <Grid size={{ xs: 12 }} key={observer.id}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 2,
                            p: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                            <Grid size={{ xs: 12, md: 4 }}>
                              <FormTextField
                                name={`observers.${index}.name`}
                                label={t("signatures.fullName", "Nome completo")}
                                fullWidth
                                size="small"
                                required
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                              <FormTextField
                                name={`observers.${index}.email`}
                                label="E-mail"
                                type="email"
                                fullWidth
                                size="small"
                                required
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                              <Controller
                                name={`observers.${index}.documentos`}
                                control={control}
                                defaultValue={[]}
                                render={({ field: { value, onChange } }) => (
                                  <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    size="small"
                                    options={documentOptions}
                                    value={value || []}
                                    onChange={(_, newValue) => onChange(newValue)}
                                    renderOption={(props, option, { selected }) => {
                                      const { key, ...optionProps } = props;
                                      return (
                                        <li key={key} {...optionProps}>
                                          <Checkbox
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                            size="small"
                                          />
                                          <ListItemText primary={option} />
                                        </li>
                                      );
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label={t(
                                          "signatures.allowedDocuments",
                                          "Documentos permitidos",
                                        )}
                                        placeholder={
                                          !value || value.length === 0
                                            ? t(
                                                "signatures.allDocumentsAllowed",
                                                "Todos os documentos (Padrão)",
                                              )
                                            : ""
                                        }
                                      />
                                    )}
                                  />
                                )}
                              />
                              <FormHelperText
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  mt: 0.5,
                                }}
                              >
                                <Tooltip
                                  title={t(
                                    "signatures.observerDocsTooltip",
                                    "Caso nenhum documento seja selecionado, o observador poderá visualizar todos os documentos do envelope.",
                                  )}
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <InfoIcon
                                      sx={{ fontSize: 16 }}
                                      color="action"
                                    />
                                  </Box>
                                </Tooltip>
                                <span>
                                  {t(
                                    "signatures.observerDocsHelper",
                                    "Sem seleção, o observador visualiza todos os documentos.",
                                  )}
                                </span>
                              </FormHelperText>
                            </Grid>
                          </Grid>
                          <IconButton
                            color="error"
                            onClick={() => removeObserver(index)}
                            sx={{ mt: 0.5 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() =>
                    navigate({
                      to: "/signatures/envelopes",
                      search: { page: 1, size: 10 },
                    })
                  }
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t("common.saving")
                    : t("signatures.submitEnvelope")}
                </Button>
              </Box>
            </Box>
          </form>
        </FormProvider>
      </Paper>

      <DocumentSelectionDialog
        open={isDocumentDialogOpen}
        onClose={() => setIsDocumentDialogOpen(false)}
        onSelect={handleDocumentsSelected}
        initialSelectedDocs={getValues("documents") as any}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ListLayout>
  );
}
